import type { DragEndEvent } from '@dnd-kit/core';

import type { CardHealth } from '@/lib/card-health';

/**
 * Resolve um evento de drag-end do @dnd-kit em uma mutação concreta (ou null se for no-op).
 *
 * Versão GENÉRICA do helper original em `src/pages/menu/comercial/kanban-utils.ts`.
 * Aceita qualquer tipo `T` com `id: string` + função `getStage(item)` que extrai o
 * slug atual da coluna. Trabalho com cards de OS (`ServiceOrder.status`), Deals
 * (`Deal.fase`), e qualquer outro workflow futuro.
 *
 * Cobre 4 casos de no-op:
 *   - solto fora de qualquer coluna (over=null)
 *   - id não existe na lista (drag de id estranho)
 *   - destino é a mesma coluna do item (drop na própria coluna)
 *   - destino não é uma coluna válida (não está em `validColumnSlugs`)
 *
 * `validColumnSlugs` é injetado pelo caller — combinação de stages padrão +
 * colunas custom criadas via `useKanbanColumns.addColumn`. Sem isso, drop pra
 * slug inexistente passaria como move válido (regressão crítica).
 */
export function resolveKanbanDrop<T extends { id: string }>(
  event: Pick<DragEndEvent, 'active' | 'over'>,
  items: T[],
  getStage: (item: T) => string,
  validColumnSlugs: ReadonlySet<string>
): { itemId: string; novaFase: string } | null {
  if (!event.over) return null;
  const itemId = String(event.active.id);
  const candidate = String(event.over.id);
  if (!validColumnSlugs.has(candidate)) return null;
  const item = items.find((i) => i.id === itemId);
  if (!item || getStage(item) === candidate) return null;
  return { itemId, novaFase: candidate };
}

/**
 * Conta itens por health usando getter genérico.
 *
 * Wrapper sobre `card-health.countByHealth` (que é específico de `Deal`).
 * Aqui aceita qualquer tipo `T` + função de extração.
 */
export function countItemsByHealth<T>(
  items: T[],
  getHealth: (item: T) => CardHealth | null
): Record<CardHealth, number> {
  const counts: Record<CardHealth, number> = { overdue: 0, idle: 0, healthy: 0 };
  for (const i of items) {
    const h = getHealth(i);
    if (h) counts[h] += 1;
  }
  return counts;
}
